import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useServerFn } from "@tanstack/react-start";
import { claimGuestProjects } from "@/lib/studio-claim.functions";
import { getAnonToken } from "@/lib/studio";
import { z } from "zod";

export const Route = createFileRoute("/auth")({
  head: () => ({ meta: [{ title: "Sign in — Michael's Painting" }] }),
  validateSearch: (s: Record<string, unknown>) =>
    z.object({ redirect: z.string().optional() }).parse(s),
  component: AuthRoute,
  errorComponent: ({ error }) => <div className="py-32 text-center text-red-400">{error.message}</div>,
  notFoundComponent: () => <div className="py-32 text-center text-zinc-400">Not found.</div>,
});

function AuthRoute() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/auth" });
  const claim = useServerFn(claimGuestProjects);
  const [mode, setMode] = useState<"signin" | "signup" | "forgot">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setError(null); setInfo(null);
    try {
      if (mode === "forgot") {
        const { error: e2 } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (e2) throw e2;
        setInfo("If an account exists for that email, a reset link is on its way.");
        return;
      }
      const fn = mode === "signin"
        ? supabase.auth.signInWithPassword({ email, password })
        : supabase.auth.signUp({
            email,
            password,
            options: {
              emailRedirectTo: `${window.location.origin}${
                search.redirect && search.redirect.startsWith("/") && !search.redirect.startsWith("//")
                  ? search.redirect
                  : "/"
              }`,
            },
          });
      const { error: e2 } = await fn;
      if (e2) throw e2;
      // Migrate any guest studio projects to this account.
      try {
        const anon = getAnonToken();
        if (anon) await claim({ data: { anonToken: anon } });
      } catch {
        // Non-fatal — projects stay accessible via anon token in localStorage.
      }
      const redirect =
        search.redirect && search.redirect.startsWith("/") && !search.redirect.startsWith("//")
          ? search.redirect
          : "/";
      navigate({ to: redirect });
    } catch (e2) { setError((e2 as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <section className="min-h-screen bg-[#030303] text-white grid place-items-center px-6 py-24">
      <div className="w-full max-w-md border border-white/10 rounded-2xl bg-white/[0.02] p-8">
        <h1 className="font-serif text-3xl mb-2">{mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Reset password"}</h1>
        <p className="text-sm text-zinc-500 mb-6">{mode === "forgot" ? "We'll email you a link to set a new password." : "Admin access for estimate requests."}</p>
        <form onSubmit={submit} className="space-y-3">
          <input type="email" autoComplete="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
          {mode !== "forgot" && (
            <input type="password" autoComplete={mode === "signin" ? "current-password" : "new-password"} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-3 text-[16px] focus:border-amber-300/50 outline-none" />
          )}
          {error && <p className="text-sm text-red-400">{error}</p>}
          {info && <p className="text-sm text-emerald-400">{info}</p>}
          <button type="submit" disabled={busy} className="w-full bg-amber-300 text-zinc-950 font-semibold py-3 rounded-full text-sm hover:bg-amber-200 disabled:opacity-40 inline-flex items-center justify-center gap-2">
            {busy && <Loader2 className="size-4 animate-spin" />}
            {mode === "signin" ? "Sign in" : mode === "signup" ? "Create account" : "Send reset link"}
          </button>
        </form>
        <div className="mt-4 flex flex-col items-center gap-2">
          {mode === "signin" && (
            <button onClick={() => { setMode("forgot"); setError(null); setInfo(null); }} className="text-xs text-zinc-500 hover:text-white">
              Forgot password?
            </button>
          )}
          <button onClick={() => { setMode((m) => m === "signin" ? "signup" : "signin"); setError(null); setInfo(null); }} className="text-xs text-zinc-500 hover:text-white">
            {mode === "signup" ? "Have an account? Sign in" : mode === "forgot" ? "Back to sign in" : "Need an account? Create one"}
          </button>
        </div>
      </div>
    </section>
  );
}